/**
 * 직원 앱 배포 온보딩 (MED-792 · 793 · 795) — admin 「앱 배포 온보딩」 탭과 「앱 안내 발송」 모달.
 *
 * 화면은 두 컴포넌트다:
 *  - onboardingPage()      체크리스트 7단계 · 팀별 집계 · 직원별 현황(재안내 대상 선택) · 설정 · 이력 · 공지 · 보고서
 *  - appGuideSendModal()   안내 발송 모달 — 직원 관리 선택 툴바와 온보딩 화면이 같은 모달을 연다
 *                          (`app-guide-send-open` window 이벤트로 열고, 끝나면 `app-guide-sent` 를 쏜다)
 *
 * 판정(단계·집계)은 전부 서버(app-onboarding.service)가 하고, 여기서는 그리기만 한다 —
 * 직원 관리 「앱 상태」 열과 이 화면의 숫자가 다르면 안 되기 때문이다.
 */
import { askConfirm, showError } from "@hereby/components";
import {
  APP_HOSPITAL_CODE_PATTERN,
  APP_ONBOARDING_STAGE_LABELS,
  APP_ONBOARDING_STAGE_ORDER,
  APP_ONBOARDING_STAGE_SHORT_LABELS,
  APP_ONBOARDING_TARGET_RATE_MAX,
  APP_ONBOARDING_TARGET_RATE_MIN,
  APP_TUTORIAL_STEP_DEFS,
  appGuideTemplateIncludesPassword,
  DEFAULT_APP_GUIDE_EMAIL_TEMPLATE,
  DEFAULT_APP_GUIDE_SMS_TEMPLATE,
  DEFAULT_APP_ONBOARDING_NOTICE,
  findUnknownAppGuidePlaceholders,
  renderAppGuideTemplate,
  WORKS_APP_STORE_URL,
  WORKS_PLAY_STORE_URL,
} from "@hereby/core/constants";
import {
  AppGuideChannel,
  AppGuideTargetMode,
  AppOnboardingChecklistStep,
  AppOnboardingStage,
  type AppTutorialStep,
} from "@hereby/core/enums";
import { fetchAllPages, type HerebyClient } from "@hereby/sdk";
import Alpine from "alpinejs";

type Sdk = HerebyClient;
export type OnboardingSummary = Awaited<ReturnType<Sdk["appOnboarding"]["summary"]>>;
export type OnboardingStatusPage = Awaited<ReturnType<Sdk["appOnboarding"]["listStatus"]>>;
export type OnboardingStatusRow = OnboardingStatusPage["items"][number];
export type OnboardingReport = Awaited<ReturnType<Sdk["appOnboarding"]["report"]>>;
export type GuidePreview = Awaited<ReturnType<Sdk["appOnboarding"]["previewGuide"]>>;
export type GuideSendResult = Awaited<ReturnType<Sdk["appOnboarding"]["sendGuide"]>>;
export type GuideHistoryPage = Awaited<ReturnType<Sdk["appOnboarding"]["sendHistory"]>>;

// ─── 순수 도우미(테스트 대상) ────────────────────────────────────────────────

/** 단계 → DS 라벨 톤. 사다리 위로 갈수록 초록에 가깝다. */
export function stageLabelClass(stage: string): string {
  switch (stage) {
    case AppOnboardingStage.NOT_SENT:
      return "ds-label--light-gray";
    case AppOnboardingStage.SENT:
      return "ds-label--yellow";
    case AppOnboardingStage.INSTALLED:
      return "ds-label--blue";
    case AppOnboardingStage.LOGGED_IN:
      return "ds-label--primary";
    case AppOnboardingStage.PASSWORD_CHANGED:
      return "ds-label--aqua";
    case AppOnboardingStage.TUTORIAL_DONE:
      return "ds-label--green";
    default:
      return "ds-label--gray";
  }
}

export function stageLabel(stage: string): string {
  return APP_ONBOARDING_STAGE_LABELS[stage as AppOnboardingStage] ?? stage;
}

export function stageShortLabel(stage: string): string {
  return APP_ONBOARDING_STAGE_SHORT_LABELS[stage as AppOnboardingStage] ?? stage;
}

/** 직원 관리 「앱 상태」 필터 옵션 — 사다리 순서 */
export const APP_STAGE_FILTER_OPTIONS = APP_ONBOARDING_STAGE_ORDER.map((id) => ({
  id,
  label: APP_ONBOARDING_STAGE_SHORT_LABELS[id],
}));

export function checklistStateLabel(state: string): string {
  return (
    { done: "완료", partial: "진행 중", pending: "할 일", not_applicable: "해당 없음" }[state] ??
    state
  );
}

export function checklistStateClass(state: string): string {
  return (
    {
      done: "ds-label--green",
      partial: "ds-label--yellow",
      pending: "ds-label--primary",
      not_applicable: "ds-label--light-gray",
    }[state] ?? "ds-label--gray"
  );
}

// ─── 도입 흐름 묶음 (2026-10-13 개편) ────────────────────────────────────────
// 체크리스트 7단계를 실제 판정 규칙대로 묶어 본다 — 순서가 걸린 곳은 「도입 설정」(회사 코드)과 「발송」뿐이라서다.
//   도입 설정(회사 코드 — 없으면 앱 로그인도 안내 발송도 안 된다) → 준비(연락처 · 비밀번호, 순서 무관 · 발송 전 권장)
//   → 발송 → 확인 ↔ 재안내(목표까지 반복) → 완료
// 공지는 흐름 밖(언제든). 단계 판정 자체는 서버(app-onboarding.service summarizeAppOnboarding) 값을 그대로 쓴다.

export type OnboardingPhaseKey = "setup" | "prepare" | "send" | "follow" | "complete";
/** done 완료 · attention 지났지만 덜 끝남(진행 중/확인 필요) · current 지금 할 일 · next 아직 차례 아님 */
export type OnboardingPhaseState = "done" | "attention" | "current" | "next";

export interface OnboardingPhaseInput {
  counts: { total: number; missingContact: number; sent: number; sendFailed: number; rate: number };
  steps: { key: string; state: string; enabled: boolean }[];
  achieved: boolean;
  targetRate: number;
  completedAt: string | Date | null;
  /** 실제로 쓰이는 회사 코드(도입 설정 · system-admin 동기화 · 서버 환경변수 중 이긴 값) — 없으면 null */
  hospitalCode: string | null;
}

export interface OnboardingPhase {
  key: OnboardingPhaseKey;
  order: number;
  label: string;
  sub: string;
  state: OnboardingPhaseState;
  /** 지난 단계면 한 일 요약, 다음 단계면 그 단계에서 할 일 */
  text: string;
  /** 다음 단계가 열리는 때 */
  when: string;
}

/** 지금 할 일 묶음 — 완료 처리했으면 finished. 회사 코드가 없으면 무엇보다 먼저 도입 설정.
 *  준비는 발송을 막지 않으므로 지금 할 일이 되지 않는다. */
export function currentOnboardingPhase(
  s: Pick<OnboardingPhaseInput, "counts" | "achieved" | "completedAt" | "hospitalCode">,
): Exclude<OnboardingPhaseKey, "prepare"> | "finished" {
  if (s.completedAt) {
    return "finished";
  }
  if (!s.hospitalCode) {
    return "setup";
  }
  if (s.counts.sent === 0) {
    return "send";
  }
  return s.achieved ? "complete" : "follow";
}

const PHASE_ORDER: OnboardingPhaseKey[] = ["setup", "prepare", "send", "follow", "complete"];

export function buildOnboardingPhases(s: OnboardingPhaseInput): OnboardingPhase[] {
  const cur = currentOnboardingPhase(s);
  const curIdx = cur === "finished" ? PHASE_ORDER.length : PHASE_ORDER.indexOf(cur);
  const stepState = (key: string) => s.steps.find((x) => x.key === key && x.enabled)?.state;
  const notDone = (...keys: string[]) =>
    keys.some((k) => {
      const st = stepState(k);
      return st !== undefined && st !== "done";
    });
  const c = s.counts;
  const contactText =
    c.missingContact > 0
      ? `${c.total - c.missingContact}명 연락처 있음 · ${c.missingContact}명 없음`
      : `${c.total}명 모두 연락처 있음`;
  const passwordText =
    stepState("password_policy") === "done" ? "임시 비밀번호 정책 있음" : "임시 비밀번호 기본값";
  const defs: Record<OnboardingPhaseKey, Omit<OnboardingPhase, "key" | "order" | "state">> = {
    setup: {
      label: "도입 설정",
      sub: "회사 코드 · 문구",
      text: s.hospitalCode
        ? `회사 코드 ${s.hospitalCode} · 목표 ${s.targetRate}%`
        : "회사 코드가 설정되지 않았어요.",
      when: "가장 먼저",
    },
    prepare: {
      label: "준비",
      sub: "연락처 · 비밀번호",
      text: `${contactText} · ${passwordText}`,
      when: "언제든",
    },
    send: {
      label: "발송",
      sub: "앱 안내",
      text:
        c.sent > 0
          ? `${c.sent}명에게 보냄${c.sendFailed > 0 ? ` · 실패 ${c.sendFailed}명` : ""}`
          : "연락처가 있는 직원에게 설치 링크와 임시 비밀번호를 문자 · 이메일로 보내요.",
      when: "준비를 확인한 뒤 바로",
    },
    follow: {
      label: "확인 · 재안내",
      sub: "목표까지 반복",
      text:
        cur === "complete" || cur === "finished"
          ? `첫 로그인 ${c.rate}%${s.achieved ? " — 목표 달성" : ""}`
          : "팀별 설치 · 로그인을 보고, 로그인하지 않은 직원에게 다시 안내해요. 목표에 닿을 때까지 되풀이해요.",
      when: "안내를 보내면 바로 열려요",
    },
    complete: {
      label: "완료",
      sub: `목표 ${s.targetRate}%`,
      text:
        cur === "finished"
          ? "직원 앱 도입을 완료 처리했어요."
          : "완료 처리하면 이 메뉴는 설정 안으로 들어가요. 배포 완료 보고(팀별 수치)를 내려받을 수 있어요.",
      when: `첫 로그인 ${s.targetRate}%에 닿으면 열려요 — 그 전에도 오른쪽 위 「완료 처리」로 끝낼 수 있어요`,
    },
  };
  return PHASE_ORDER.map((key, i) => {
    let state: OnboardingPhaseState;
    if (key === "prepare") {
      // 준비는 차례를 기다리지 않는다 — 덜 끝났으면 「확인 필요」로만 알린다
      state = notDone("contacts", "password_policy") ? "attention" : "done";
    } else if (i < curIdx) {
      state =
        key === "send" && notDone("send_guide")
          ? "attention"
          : key === "follow" && notDone("install_status", "resend") && !s.achieved
            ? "attention"
            : "done";
    } else {
      state = i === curIdx ? "current" : "next";
    }
    return { key, order: i + 1, state, ...defs[key] };
  });
}

/** 지금 할 일 묶음의 이름 — 화면 위 직원 앱 도입 띠(dashboard.html)가 「지금 할 일」로 쓴다 */
export function currentOnboardingPhaseLabel(
  s: Pick<OnboardingPhaseInput, "counts" | "achieved" | "completedAt" | "hospitalCode">,
): string {
  return {
    setup: "도입 설정",
    send: "안내 발송",
    follow: "확인 · 재안내",
    complete: "완료 처리",
    finished: "완료",
  }[currentOnboardingPhase(s)];
}

export function phaseStateLabel(phase: Pick<OnboardingPhase, "key" | "state">): string {
  if (phase.state === "attention") {
    return phase.key === "prepare" ? "확인 필요" : "진행 중";
  }
  return { done: "완료", current: "지금 할 일", next: "다음" }[phase.state];
}

/**
 * 발송 실패 사유 — 문자 · 이메일 서비스가 돌려준 영문 오류를 화면용 한글로 바꾼다.
 * 이력 행에는 서비스 응답이 그대로 남아 있어(sms.service · email.service) 화면에서만 옮긴다.
 * 모르는 문구는 그대로 보여 준다(공급자가 준 한글 사유 · 시드 문구 포함).
 */
export function sendErrorLabel(error: string | null | undefined): string {
  if (!error) {
    return "";
  }
  if (error === "SMS service not configured") {
    return "문자 발송 설정이 없어요(서버)";
  }
  if (error === "Email service not configured") {
    return "이메일 발송 설정이 없어요(서버)";
  }
  if (error.startsWith("Invalid phone number")) {
    return "휴대폰 번호 형식이 올바르지 않아요";
  }
  const api = /^SMS API error: (\d+)/.exec(error);
  if (api) {
    return `문자 서비스 오류(${api[1]})`;
  }
  if (error === "Failed to send SMS") {
    return "문자를 보내지 못했어요";
  }
  return error;
}

// ─── 도입 설정 팝업 — 안내 문구 미리보기 ─────────────────────────────────────
const PREVIEW_MARK_OPEN = "\u0001";
const PREVIEW_MARK_CLOSE = "\u0002";

/**
 * 안내 문구 미리보기 HTML — 서버 발송과 같은 renderAppGuideTemplate 로 치환하고, 바뀌어 들어간 값만
 * 강조 span 으로 감싼다(값 앞뒤에 제어 문자 표식을 넣어 치환한 뒤 이스케이프하고 span 으로 바꾼다).
 * 회사 코드가 없으면 서버처럼 그 줄이 빠진다 — 화면은 그 사실을 따로 알린다.
 */
export function appGuidePreviewHtml(
  template: string,
  vars: {
    organizationName: string;
    employeeName: string;
    loginId: string;
    temporaryPassword: string;
    downloadUrl: string | null;
    hospitalCode: string | null;
  },
): string {
  const mark = (v: string) => `${PREVIEW_MARK_OPEN}${v}${PREVIEW_MARK_CLOSE}`;
  const text = renderAppGuideTemplate(template, {
    organizationName: mark(vars.organizationName),
    employeeName: mark(vars.employeeName),
    loginId: mark(vars.loginId),
    temporaryPassword: mark(vars.temporaryPassword),
    hospitalCode: vars.hospitalCode ? mark(vars.hospitalCode) : null,
    downloadUrl: vars.downloadUrl ? mark(vars.downloadUrl) : null,
  });
  return escapeHtml(text)
    .split(PREVIEW_MARK_OPEN)
    .join('<span class="ob-preview-value">')
    .split(PREVIEW_MARK_CLOSE)
    .join("</span>");
}

// ─── 빈칸 문구 — 「-」 대신 왜 비었는지(UX 라이팅 2026-10-13) ───────────────────

/** 직원별 현황 「안내 발송」 칸이 비었을 때 */
export function guideSentEmptyText(r: {
  hasPhone: boolean;
  hasEmail: boolean;
  lastSendStatus?: string | null;
}): string {
  if (!(r.hasPhone || r.hasEmail)) {
    return "연락처 없음";
  }
  if (r.lastSendStatus === "failed") {
    return "보내지 못함";
  }
  return "보내지 않음";
}

/** 직원별 현황 「마지막 접속」 칸이 비었을 때 — 안내 전 · 로그인 전 · (로그인했는데 접속 기록이 없으면) 기록 없음 */
export function lastSeenEmptyText(r: {
  stage: string;
  guideSentAt?: string | Date | null;
}): string {
  if (r.stage === AppOnboardingStage.NOT_SENT && !r.guideSentAt) {
    return "안내 전";
  }
  if (
    r.stage === AppOnboardingStage.SENT ||
    r.stage === AppOnboardingStage.INSTALLED ||
    r.stage === AppOnboardingStage.NOT_SENT
  ) {
    return "로그인 전";
  }
  return "기록 없음";
}

/** 직원 관리 「앱 상태」 셀 툴팁 — 발송·접속 사실을 한 줄씩 */
export function appStatusTooltip(
  app: {
    stageLabel: string;
    guideSentAt?: string | Date | null;
    guideSendCount?: number;
    lastSendStatus?: string | null;
    lastSendError?: string | null;
    firstLoginAt?: string | Date | null;
    lastSeenAt?: string | Date | null;
    platform?: string | null;
    appVersion?: string | null;
    hasContact?: boolean;
  } | null,
  formatDateTime: (d: string | Date) => string,
): string {
  if (!app) {
    return "";
  }
  const lines = [app.stageLabel];
  if (app.hasContact === false) {
    lines.push("휴대폰·이메일 없음 — 안내를 받을 수 없어요");
  }
  if (app.guideSentAt) {
    lines.push(`안내 발송 ${formatDateTime(app.guideSentAt)} (${app.guideSendCount ?? 1}회)`);
  }
  if (app.lastSendStatus === "failed") {
    lines.push(
      `마지막 발송 실패${app.lastSendError ? `: ${sendErrorLabel(app.lastSendError)}` : ""}`,
    );
  }
  if (app.firstLoginAt) {
    lines.push(`첫 로그인 ${formatDateTime(app.firstLoginAt)}`);
  }
  if (app.lastSeenAt) {
    const client = [app.platform, app.appVersion].filter(Boolean).join(" ");
    lines.push(`마지막 접속 ${formatDateTime(app.lastSeenAt)}${client ? ` · ${client}` : ""}`);
  }
  return lines.join("\n");
}

const BANNER_DISMISS_DAYS = 7;

export function onboardingBannerKey(
  organizationId?: string | null,
  userId?: string | null,
): string {
  return `admin:appOnboardingBanner:${organizationId || "org"}:${userId || "me"}`;
}

/** 마지막으로 확인한 「도입 끝남」 값을 기억하는 키 — 조직별. 새로고침 때 LNB 가 서버 응답 전에 이 값으로 먼저
 *  그려져, 끝난 조직에서 「시작하기」 메뉴가 잠깐 보였다 사라지지 않게 한다(편의용 — 서버 응답이 늘 이긴다). */
export function appAdoptionFinishedKey(organizationId?: string | null): string {
  return `admin:appAdoptionFinished:${organizationId || "org"}`;
}

/**
 * 직원 앱 도입이 끝났는지 — 목표 비율 달성(자동) 또는 관리자 완료 처리. 체크리스트 7단계 「완료」와 같은 기준.
 * 끝나면 LNB 「시작하기」 메뉴가 사라지고 설정 › 「직원 앱 도입」 탭(내 정보 옆)에서 다시 본다. 배너도 이때 숨는다.
 */
export function isAppAdoptionFinished(
  summary: { achieved: boolean; completedAt: string | null } | null,
): boolean {
  return Boolean(summary && (summary.achieved || summary.completedAt));
}

/**
 * 상단 진행률 배너를 보일지 — 목표 달성·완료 처리·최근 7일 안에 닫음이면 숨긴다.
 * (MED-795 「이후 상단 배너에 진행률 표시, 완료 후 숨김」)
 */
export function shouldShowOnboardingBanner(
  summary: { achieved: boolean; completedAt: string | null; counts: { total: number } } | null,
  dismissedAt: string | null,
  now: Date = new Date(),
): boolean {
  if (!summary || summary.counts.total === 0) {
    return false;
  }
  if (isAppAdoptionFinished(summary)) {
    return false;
  }
  if (dismissedAt) {
    const t = Date.parse(dismissedAt);
    if (!Number.isNaN(t) && now.getTime() - t < BANNER_DISMISS_DAYS * 86_400_000) {
      return false;
    }
  }
  return true;
}

/** 배포 완료 보고 xlsx 의 시트 3장(요약 · 팀별 · 직원별) — 행 배열 */
export function buildReportSheets(
  report: OnboardingReport,
  formatDateTime: (d: string | Date | null | undefined) => string,
): { name: string; rows: (string | number)[][] }[] {
  const c = report.counts;
  const summary: (string | number)[][] = [
    ["항목", "값"],
    ["생성 시각", formatDateTime(report.generatedAt)],
    ["목표(%)", report.targetRate],
    ["달성 여부", report.achieved ? "달성" : "미달성"],
    ["재직 직원", c.total],
    ["연락처 없음", c.missingContact],
    ["안내 발송", c.sent],
    ["앱 설치", c.installed],
    ["첫 로그인", c.loggedIn],
    ["비밀번호 변경", c.passwordChanged],
    ["튜토리얼 완료", c.tutorialDone],
    ["첫 로그인 이상(%)", c.rate],
  ];
  const byTeam: (string | number)[][] = [
    [
      "팀",
      "인원",
      "안내 발송",
      "앱 설치",
      "첫 로그인",
      "비밀번호 변경",
      "튜토리얼 완료",
      "로그인율(%)",
    ],
    ...report.byTeam.map((t) => [
      t.teamName,
      t.total,
      t.sent,
      t.installed,
      t.loggedIn,
      t.passwordChanged,
      t.tutorialDone,
      t.rate,
    ]),
  ];
  const employees: (string | number)[][] = [
    [
      "사번",
      "이름",
      "팀",
      "앱 상태",
      "연락처",
      "안내 발송",
      "발송 횟수",
      "앱 설치",
      "첫 로그인",
      "비밀번호 변경",
      "튜토리얼 완료",
      "플랫폼",
      "앱 버전",
      "마지막 접속",
    ],
    ...report.employees.map((e) => [
      e.employeeNumber ?? "",
      e.name,
      e.teamNames.join(", "),
      e.stageLabel,
      e.hasContact ? "있음" : "없음",
      formatDateTime(e.guideSentAt),
      e.guideSendCount,
      formatDateTime(e.appInstalledAt),
      formatDateTime(e.firstLoginAt),
      e.passwordChanged ? "완료" : "",
      formatDateTime(e.tutorialCompletedAt),
      e.platform ?? "",
      e.appVersion ?? "",
      formatDateTime(e.lastSeenAt),
    ]),
  ];
  return [
    { name: "요약", rows: summary },
    { name: "팀별", rows: byTeam },
    { name: "직원별", rows: employees },
  ];
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** 종이 안내문(QR) — 인쇄 창에 그대로 쓴다. 이메일처럼 앱 밖 문서라 인라인 스타일을 쓴다. */
export function paperGuideHtml(opts: {
  organizationName: string;
  /** 앱 첫 실행 「조직 선택」 에 입력하는 회사 코드 — 없으면 인사 담당자 문의로 안내 */
  hospitalCode: string | null;
  link: string | null;
  qrDataUrl: string | null;
  qrTargets: { label: string; dataUrl: string }[];
}): string {
  const single = opts.link
    ? `<p class="link">${escapeHtml(opts.link)}</p>${opts.qrDataUrl ? `<img class="qr" src="${opts.qrDataUrl}" alt="QR" />` : ""}`
    : `<div class="stores">${opts.qrTargets
        .map(
          (t) =>
            `<div class="store"><img class="qr" src="${t.dataUrl}" alt="${escapeHtml(t.label)} QR" /><p>${escapeHtml(t.label)}</p></div>`,
        )
        .join("")}</div>`;
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8" /><title>직원 앱 설치 안내</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Malgun Gothic", sans-serif; color: #1b1b1b; margin: 0; padding: 32px; }
  h1 { font-size: 26px; margin: 0 0 8px; }
  h2 { font-size: 18px; margin: 28px 0 8px; }
  p { font-size: 16px; line-height: 1.7; margin: 0 0 8px; }
  .org { font-size: 14px; color: #666; margin-bottom: 24px; }
  .link { font-size: 15px; word-break: break-all; }
  .qr { width: 220px; height: 220px; display: block; margin: 12px 0; }
  .stores { display: flex; gap: 40px; }
  .store p { text-align: center; font-weight: 600; }
  .code { display: inline-block; font-size: 28px; font-weight: 700; letter-spacing: 0.08em; border: 2px solid #1b1b1b; border-radius: 8px; padding: 6px 18px; margin: 8px 0 4px; }
  ol { font-size: 16px; line-height: 1.8; padding-left: 20px; }
  @media print { body { padding: 16px; } }
</style></head><body>
<p class="org">${escapeHtml(opts.organizationName)}</p>
<h1>직원 앱(hereby Works) 설치 안내</h1>
<p>근무표·출퇴근·휴가 신청·급여명세서를 휴대폰에서 볼 수 있어요. 아래 QR 을 카메라로 찍어 앱을 설치해 주세요.</p>
${single}
<h2>회사 코드</h2>
${
  opts.hospitalCode
    ? `<p>앱을 처음 열면 회사 코드를 물어요. 아래 코드를 입력해 주세요.</p><p class="code">${escapeHtml(opts.hospitalCode)}</p>`
    : "<p>앱을 처음 열면 회사 코드를 물어요. 회사 코드는 인사 담당자에게 확인해 주세요.</p>"
}
<h2>로그인 방법</h2>
<ol>
  <li>앱을 열고 <b>회사 코드</b> 를 입력한 뒤 「맞아요, 연결하기」 를 눌러요.</li>
  <li><b>로그인 ID(사번)</b> 와 <b>임시 비밀번호</b> 를 입력해요. 안내 문자·이메일을 못 받았다면 인사 담당자에게 물어보세요.</li>
  <li>첫 로그인 뒤 <b>비밀번호를 꼭 바꿔 주세요.</b></li>
  <li>알림을 허용하면 근무표 변경·결재 결과를 바로 받아요.</li>
</ol>
<h2>문의</h2>
<p>로그인이 안 되면 인사 담당자에게, 앱 사용 중 불편한 점은 앱의 「더보기 › 불편·기능 신고」 로 알려 주세요.</p>
<script>window.addEventListener("load", function () { setTimeout(function () { window.print(); }, 150); });</script>
</body></html>`;
}

// ─── 상태 유틸 ───────────────────────────────────────────────────────────────

function sdkOf(): Sdk {
  return Alpine.store("hereby").sdk as Sdk;
}

function toast() {
  return Alpine.store("toast");
}

function fmtDateTime(d: string | Date | null | undefined): string {
  if (!d) {
    return "";
  }
  return Alpine.store("hereby").formatDateTime(d as string | Date);
}

/** 앵커 이동 뒤 탭 밑선(= 본문 위끝)과 묶음 제목 줄 사이 간격(px) — 본문 윗여백(24)과 같아 처음 열었을 때와 같은 자리 */
const SETTINGS_ANCHOR_GAP = 24;

/** 도입 설정 팝업의 스크롤 본문 */
function settingsBody(): HTMLElement | null {
  return document.querySelector<HTMLElement>("[data-testid=onboarding-settings-body]");
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

interface StatusFilter {
  teamIds: string[];
  stages: string[];
  missingContact: boolean;
  search: string;
}

// ─── 온보딩 페이지 컴포넌트 ─────────────────────────────────────────────────

export function onboardingPage() {
  return {
    loadedOnce: false,
    loading: false,
    summary: null as OnboardingSummary | null,
    teams: [] as { id: string; name: string }[],

    statusFilter: { teamIds: [], stages: [], missingContact: false, search: "" } as StatusFilter,
    status: null as OnboardingStatusPage | null,
    statusLoading: false,
    selectedIds: [] as string[],
    /** 페이지 아래 「현황」 서브탭 — 팀별 / 직원별 (페이지 내 상태, URL 에 싣지 않는다) */
    statusTab: "teams" as "teams" | "employees",

    settingsOpen: false,
    /** 도입 설정 팝업 — 앵커 탭(앱 로그인 · 안내 문구 · 도입 기준). 본문을 스크롤하면 지금 보이는 묶음으로 바뀐다 */
    settingsSection: "login" as "login" | "message" | "rule",
    /** 탭을 눌러 부드럽게 옮기는 동안 스크롤 감지가 탭을 되돌리지 않게 잠깐 잠근다 */
    settingsSpyLockUntil: 0,
    /** 안내 문구 — 지금 고치는 채널(문자 / 이메일). 미리보기도 이 채널 */
    settingsChannel: "sms" as "sms" | "email",
    /** 연 시점의 폼 — 닫을 때 바뀐 게 있으면 확인창 */
    settingsBaseline: "",
    settingsSaving: false,
    /** 도입 설정 문구 편집 중 치환되지 않는 변수 경고(MED-922) */
    get smsTemplateUnknown(): string[] {
      return findUnknownAppGuidePlaceholders(this.settingsForm.smsTemplate);
    },
    get emailTemplateUnknown(): string[] {
      return findUnknownAppGuidePlaceholders(this.settingsForm.emailTemplate);
    },
    /** 문구에서 {임시비밀번호} 줄을 지웠다 — 이 문구로는 비밀번호 발급 대상에게 보낼 수 없다(MED-916) */
    get smsTemplateLacksPassword(): boolean {
      return !appGuideTemplateIncludesPassword(this.settingsForm.smsTemplate);
    },
    get emailTemplateLacksPassword(): boolean {
      return !appGuideTemplateIncludesPassword(this.settingsForm.emailTemplate);
    },

    settingsForm: {
      targetRate: 80,
      downloadUrl: "",
      hospitalCode: "",
      smsTemplate: "",
      emailTemplate: "",
      tutorialSteps: {} as Record<string, boolean>,
      checklistSteps: {} as Record<string, boolean>,
    },

    historyOpen: false,
    historyLoading: false,
    history: null as GuideHistoryPage | null,
    historyEmployeeId: "" as string,
    historyEmployeeName: "",
    /** 직원별 현황에서 고른 사람들의 이력만 볼 때 — 비어 있으면 전체(또는 historyEmployeeId 한 명) */
    historyEmployeeIds: [] as string[],

    noticeOpen: false,
    noticePosting: false,
    noticeForm: {
      title: DEFAULT_APP_ONBOARDING_NOTICE.title,
      content: DEFAULT_APP_ONBOARDING_NOTICE.content,
    },

    tutorialStepDefs: APP_TUTORIAL_STEP_DEFS,
    stageOptions: APP_STAGE_FILTER_OPTIONS,
    checklistStepKeys: Object.values(AppOnboardingChecklistStep),
    targetRateMin: APP_ONBOARDING_TARGET_RATE_MIN,
    targetRateMax: APP_ONBOARDING_TARGET_RATE_MAX,

    init() {
      window.addEventListener("app-onboarding-refresh", () => this.refresh());
      window.addEventListener("app-guide-sent", () => {
        this.selectedIds = [];
        this.refresh();
      });
    },

    stageLabel,
    stageLabelClass,
    sendErrorLabel,
    guideSentEmptyText,
    lastSeenEmptyText,
    checklistStateLabel,
    checklistStateClass,
    fmtDateTime,

    get storeLinks() {
      return { ios: WORKS_APP_STORE_URL, android: WORKS_PLAY_STORE_URL };
    },

    phaseStateLabel,

    /** 도입 흐름 네 묶음 — 서버 판정을 묶어서 보여 줄 뿐 새로 판정하지 않는다 */
    get phases(): OnboardingPhase[] {
      return this.summary ? buildOnboardingPhases(this.summary) : [];
    },

    get currentPhase(): ReturnType<typeof currentOnboardingPhase> | null {
      return this.summary ? currentOnboardingPhase(this.summary) : null;
    },

    /** 지금 할 일을 뺀 나머지 묶음 — 「전체 흐름」 카드 줄 */
    get otherPhases(): OnboardingPhase[] {
      return this.phases.filter((p) => p.state !== "current");
    },

    stepOf(key: string) {
      return (this.summary?.steps ?? []).find((s) => s.key === key && s.enabled) ?? null;
    },

    /** 현황 서브탭 — 탭 목록(숫자는 trailingText) */
    get statusTabs() {
      return [
        {
          id: "teams",
          label: "팀별 현황",
          trailingText: String((this.summary?.byTeam ?? []).filter((t) => t.teamId).length),
        },
        {
          id: "employees",
          label: "직원별 현황",
          trailingText: String(this.summary?.counts.total ?? 0),
        },
      ];
    },

    /** 위 카드의 「팀별 현황」 등 — 아래 현황 서브탭으로 내려간다 */
    goStatusTab(id: "teams" | "employees") {
      this.statusTab = id;
      requestAnimationFrame(() => this.scrollToStatus());
    },

    /** 이 화면의 스크롤러(onboarding-page) 하나만 움직인다 — scrollIntoView 는 admin 레이아웃까지 밀어 헤더가 올라간다 */
    scrollToStatus() {
      const page = document.querySelector<HTMLElement>("[data-testid=onboarding-page]");
      const section = document.querySelector<HTMLElement>(
        "[data-testid=onboarding-status-section]",
      );
      if (!(page && section)) {
        return;
      }
      const top =
        section.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop;
      page.scrollTo({ top: Math.max(0, top - 16), behavior: "smooth" });
    },

    /** 도입 설정 팝업을 연다 — 「도입 설정」 버튼 · 단계 버튼. focusCode 면 회사 코드 칸에 커서를 둔다 */
    openSettings(focusCode = false) {
      this.hydrateSettingsForm();
      this.settingsBaseline = JSON.stringify(this.settingsForm);
      this.settingsSection = "login";
      this.settingsChannel = "sms";
      this.settingsOpen = true;
      requestAnimationFrame(() => {
        settingsBody()?.scrollTo({ top: 0 });
        if (focusCode) {
          document
            .querySelector<HTMLInputElement>("[data-testid=onboarding-hospital-code]")
            ?.focus({ preventScroll: true });
        }
      });
    },

    get settingsDirty(): boolean {
      return this.settingsOpen && JSON.stringify(this.settingsForm) !== this.settingsBaseline;
    },

    /** 닫기(X · 취소 · Esc) — 바꾼 게 있으면 한 번 묻는다 */
    async closeSettings() {
      if (
        this.settingsDirty &&
        !(await askConfirm({
          message: "저장하지 않은 변경이 있어요. 닫으면 바꾼 내용이 사라져요.",
          confirmLabel: "닫기",
        }))
      ) {
        return;
      }
      this.settingsOpen = false;
    },

    /** 저장할 수 있나 — 실제로 쓰일 회사 코드가 하나는 있어야 한다(직접 입력 · system-admin 동기화 · 환경변수) */
    get settingsCanSave(): boolean {
      if (this.hospitalCodeInvalid) {
        return false;
      }
      return Boolean(
        this.settingsForm.hospitalCode.trim() ||
          this.syncedHospitalCode ||
          this.summary?.hospitalCodeSource === "environment",
      );
    },

    /** 앵커 탭 — 그 묶음 머리로 본문을 옮긴다 */
    goSettingsSection(id: "login" | "message" | "rule") {
      this.settingsSection = id;
      this.settingsSpyLockUntil = Date.now() + 700;
      const body = settingsBody();
      const section = document.querySelector<HTMLElement>(`[data-settings-section="${id}"]`);
      // 묶음의 제목 줄(서브탭 · 버튼 포함)이 탭 바로 아래에 오게 — 줄 전체가 보이도록 제목 줄 기준
      const target = section?.querySelector<HTMLElement>("[data-settings-anchor]") ?? section;
      if (!(body && target)) {
        return;
      }
      const top =
        target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
      body.scrollTo({ top: Math.max(0, top - SETTINGS_ANCHOR_GAP), behavior: "smooth" });
    },

    /** 스크롤 감지 — 본문 위쪽을 지난 마지막 묶음이 지금 탭. 바닥에 닿으면 마지막 묶음 */
    onSettingsScroll(body: HTMLElement) {
      if (Date.now() < this.settingsSpyLockUntil) {
        return;
      }
      const sections = Array.from(body.querySelectorAll<HTMLElement>("[data-settings-section]"));
      if (sections.length === 0) {
        return;
      }
      const atBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 2;
      let current = sections[0];
      if (atBottom) {
        current = sections[sections.length - 1];
      } else {
        const bodyTop = body.getBoundingClientRect().top;
        for (const sec of sections) {
          const head = sec.querySelector<HTMLElement>("[data-settings-anchor]") ?? sec;
          if (head.getBoundingClientRect().top - bodyTop <= SETTINGS_ANCHOR_GAP + 24) {
            current = sec;
          }
        }
      }
      this.settingsSection = current.dataset.settingsSection as "login" | "message" | "rule";
    },

    /** 낱말 칩 — 커서 자리에 {낱말} 을 넣는다(커서가 없으면 끝에) */
    insertGuideWord(word: string) {
      const key = this.settingsChannel === "sms" ? "smsTemplate" : "emailTemplate";
      const ta = document.querySelector<HTMLTextAreaElement>(
        `[data-testid=onboarding-template-${this.settingsChannel}]`,
      );
      const token = `{${word}}`;
      const value = this.settingsForm[key];
      const start = ta?.selectionStart ?? value.length;
      const end = ta?.selectionEnd ?? value.length;
      this.settingsForm[key] = value.slice(0, start) + token + value.slice(end);
      requestAnimationFrame(() => {
        if (ta) {
          ta.focus();
          ta.setSelectionRange(start + token.length, start + token.length);
        }
      });
    },

    /** 미리보기 — 지금 폼 값으로(저장 전). 받는 사람은 직원별 현황 첫 줄, 없으면 예시 이름 */
    settingsPreviewHtml(organizationName?: string | null): string {
      const sample = this.status?.items?.[0];
      const code =
        this.settingsForm.hospitalCode.trim().toUpperCase() || this.syncedHospitalCode || null;
      return appGuidePreviewHtml(
        this.settingsChannel === "sms"
          ? this.settingsForm.smsTemplate
          : this.settingsForm.emailTemplate,
        {
          organizationName: organizationName || "우리 병원",
          employeeName: sample?.name || "홍길동",
          loginId: sample?.employeeNumber || "D0001",
          temporaryPassword: "Ab12Cd34",
          downloadUrl: this.settingsForm.downloadUrl.trim() || null,
          hospitalCode: code,
        },
      );
    },

    get settingsPreviewName(): string {
      return this.status?.items?.[0]?.name || "홍길동";
    },

    /** 마지막 접속 — 날짜 · 웹/앱만. 앱 버전(빌드 문자열)은 칸에 그리지 않고 툴팁으로 */
    lastSeenText(r: { lastSeenAt?: string | Date | null; platform?: string | null }): string {
      if (!r.lastSeenAt) {
        return "";
      }
      const where = r.platform
        ? (({ web: "웹", ios: "iOS 앱", android: "Android 앱" } as Record<string, string>)[
            r.platform
          ] ?? r.platform)
        : "";
      return fmtDateTime(r.lastSeenAt) + (where ? ` · ${where}` : "");
    },

    get progressText(): string {
      const p = this.summary?.progress;
      return p ? `${p.done}/${p.total} 단계 완료` : "";
    },

    get visibleSteps() {
      return (this.summary?.steps ?? []).filter((s) => s.enabled);
    },

    get allFilteredSelected(): boolean {
      const items = this.status?.items ?? [];
      return items.length > 0 && items.every((r) => this.selectedIds.includes(r.employeeId));
    },

    async refresh() {
      this.loadedOnce = true;
      await Promise.all([this.loadSummary(), this.loadStatus(), this.loadTeams()]);
    },

    async loadSummary() {
      this.loading = true;
      try {
        this.summary = await sdkOf().appOnboarding.summary({});
        this.hydrateSettingsForm();
      } catch (err) {
        showError(errorMessage(err, "직원 앱 도입 현황을 불러오지 못했어요."));
      } finally {
        this.loading = false;
      }
    },

    async loadTeams() {
      if (this.teams.length > 0) {
        return;
      }
      try {
        const rows = await fetchAllPages<{ id: string; name: string }>(
          (page, limit) => sdkOf().team.list({ page, limit }),
          100,
        );
        this.teams = rows.map((t) => ({ id: t.id, name: t.name }));
      } catch {
        this.teams = [];
      }
    },

    async loadStatus(page = 1) {
      this.statusLoading = true;
      try {
        this.status = await sdkOf().appOnboarding.listStatus({
          teamIds: this.statusFilter.teamIds.length ? this.statusFilter.teamIds : undefined,
          stages: this.statusFilter.stages.length
            ? (this.statusFilter.stages as AppOnboardingStage[])
            : undefined,
          missingContact: this.statusFilter.missingContact || undefined,
          search: this.statusFilter.search || undefined,
          page,
          limit: 50,
        });
      } catch (err) {
        showError(errorMessage(err, "직원별 현황을 불러오지 못했어요."));
      } finally {
        this.statusLoading = false;
      }
    },

    /** 흐름 카드 바로가기 — 필터를 걸고 아래 현황의 직원별 탭으로 */
    filterStatus(patch: Partial<StatusFilter>) {
      this.statusFilter = { ...this.statusFilter, ...patch };
      this.selectedIds = [];
      this.statusTab = "employees";
      // 목록이 도착해 다시 그려진 뒤에 옮긴다 — 먼저 옮기면 줄 수가 바뀌며 표 위치가 달라져 엉뚱한 곳에 멈췄다.
      this.loadStatus(1).then(() => requestAnimationFrame(() => this.scrollToStatus()));
    },

    resetStatusFilter() {
      this.filterStatus({ teamIds: [], stages: [], missingContact: false, search: "" });
    },

    toggleSelect(id: string) {
      this.selectedIds = this.selectedIds.includes(id)
        ? this.selectedIds.filter((x) => x !== id)
        : [...this.selectedIds, id];
    },

    toggleSelectAllFiltered() {
      const items = this.status?.items ?? [];
      if (this.allFilteredSelected) {
        const ids = new Set(items.map((r) => r.employeeId));
        this.selectedIds = this.selectedIds.filter((x) => !ids.has(x));
      } else {
        this.selectedIds = [...new Set([...this.selectedIds, ...items.map((r) => r.employeeId)])];
      }
    },

    /** 발송 모달 열기 — 직원 관리 선택 툴바와 같은 이벤트 */
    openSend(mode: AppGuideTargetMode, employeeIds?: string[]) {
      window.dispatchEvent(
        new CustomEvent("app-guide-send-open", {
          detail: {
            mode,
            employeeIds:
              employeeIds ?? (mode === AppGuideTargetMode.EXPLICIT ? this.selectedIds : undefined),
            teamIds: this.statusFilter.teamIds.length ? this.statusFilter.teamIds : undefined,
          },
        }),
      );
    },

    async openHistory(row?: { employeeId: string; name: string }) {
      this.historyOpen = true;
      this.historyEmployeeId = row?.employeeId ?? "";
      this.historyEmployeeName = row?.name ?? "";
      this.historyEmployeeIds = [];
      await this.loadHistory(1);
    },

    /** 고른 직원들의 발송 이력 — 한 명이면 그 사람 이력과 같은 화면(이름 제목 · 직원 칸 숨김) */
    async openSelectedHistory() {
      const ids = [...this.selectedIds];
      if (ids.length === 1) {
        const row = (this.status?.items ?? []).find((r) => r.employeeId === ids[0]);
        await this.openHistory(row ? { employeeId: row.employeeId, name: row.name } : undefined);
        return;
      }
      this.historyOpen = true;
      this.historyEmployeeId = "";
      this.historyEmployeeName = "";
      this.historyEmployeeIds = ids;
      await this.loadHistory(1);
    },

    async loadHistory(page = 1) {
      this.historyLoading = true;
      try {
        this.history = await sdkOf().appOnboarding.sendHistory({
          employeeId: this.historyEmployeeId || undefined,
          employeeIds: this.historyEmployeeIds.length ? this.historyEmployeeIds : undefined,
          page,
          limit: 50,
        });
      } catch (err) {
        showError(errorMessage(err, "발송 이력을 불러오지 못했어요."));
      } finally {
        this.historyLoading = false;
      }
    },

    hydrateSettingsForm() {
      const s = this.summary?.settings;
      if (!s) {
        return;
      }
      this.settingsForm = {
        targetRate: s.targetRate,
        downloadUrl: s.downloadUrl ?? "",
        hospitalCode: s.hospitalCode ?? "",
        smsTemplate: s.smsTemplate,
        emailTemplate: s.emailTemplate,
        tutorialSteps: { ...s.tutorialSteps },
        checklistSteps: { ...s.checklistSteps },
      };
    },

    resetTemplates() {
      this.settingsForm.smsTemplate = DEFAULT_APP_GUIDE_SMS_TEMPLATE;
      this.settingsForm.emailTemplate = DEFAULT_APP_GUIDE_EMAIL_TEMPLATE;
    },

    /** 도입 설정의 회사 코드 — 로그인 화면과 같은 규칙(영문 대문자·숫자·하이픈 3~32자). 빈 값은 「설정 안 함」 */
    get hospitalCodeInvalid(): boolean {
      const code = this.settingsForm.hospitalCode.trim().toUpperCase();
      return code.length > 0 && !APP_HOSPITAL_CODE_PATTERN.test(code);
    },

    /** 해석된 회사 코드(조직 설정 → system-admin 동기화 → 서버 환경변수) — 종이 안내문·안내 배너가 쓴다 */
    get hospitalCode(): string | null {
      return this.summary?.hospitalCode ?? null;
    },

    /** system-admin 이 이 조직에 동기화한 코드 — 입력란을 비워 두면 이 값이 쓰인다 */
    get syncedHospitalCode(): string | null {
      return this.summary?.syncedHospitalCode ?? null;
    },

    async saveSettings() {
      if (this.hospitalCodeInvalid) {
        showError("회사 코드는 영문·숫자·하이픈 3~32자여야 해요.");
        return;
      }
      this.settingsSaving = true;
      try {
        const rate = Math.min(
          APP_ONBOARDING_TARGET_RATE_MAX,
          Math.max(
            APP_ONBOARDING_TARGET_RATE_MIN,
            Math.round(Number(this.settingsForm.targetRate) || 80),
          ),
        );
        await sdkOf().organization.updateSettings({
          appOnboarding: {
            targetRate: rate,
            downloadUrl: this.settingsForm.downloadUrl.trim(),
            hospitalCode: this.settingsForm.hospitalCode.trim().toUpperCase(),
            smsTemplate: this.settingsForm.smsTemplate,
            emailTemplate: this.settingsForm.emailTemplate,
            tutorialSteps: { ...this.settingsForm.tutorialSteps },
            checklistSteps: { ...this.settingsForm.checklistSteps },
            // 서버가 관리하는 값은 그대로 되돌려 보낸다(updateSettings 는 얕은 병합이라 키를 빼면 지워진다)
            noticeId: this.summary?.noticeId ?? null,
            completedAt: this.summary?.completedAt ?? null,
          },
        });
        toast().success("도입 설정을 저장했어요");
        this.settingsBaseline = JSON.stringify(this.settingsForm);
        this.settingsOpen = false;
        await this.loadSummary();
      } catch (err) {
        showError(errorMessage(err, "설정을 저장하지 못했어요."));
      } finally {
        this.settingsSaving = false;
      }
    },

    openNotice() {
      this.noticeForm = {
        title: DEFAULT_APP_ONBOARDING_NOTICE.title,
        content: DEFAULT_APP_ONBOARDING_NOTICE.content
          .split("{앱링크}")
          .join(
            this.summary?.downloadUrl ??
              `iOS: ${WORKS_APP_STORE_URL}\nAndroid: ${WORKS_PLAY_STORE_URL}`,
          ),
      };
      this.noticeOpen = true;
    },

    async postNotice() {
      if (
        !(await askConfirm({
          message: "직원 앱 안내 공지를 전 직원에게 게시할까요?",
          confirmLabel: "게시",
        }))
      ) {
        return;
      }
      this.noticePosting = true;
      try {
        await sdkOf().appOnboarding.postNotice({
          title: this.noticeForm.title,
          content: this.noticeForm.content,
        });
        toast().success("공지를 게시했어요");
        this.noticeOpen = false;
        await this.loadSummary();
      } catch (err) {
        showError(errorMessage(err, "공지를 게시하지 못했어요."));
      } finally {
        this.noticePosting = false;
      }
    },

    async setCompleted(completed: boolean) {
      const message = completed
        ? "직원 앱 도입을 완료 처리할까요? 상단 배너와 왼쪽 메뉴의 「직원 앱 도입」이 사라지고 체크리스트는 완료로 표시돼요. 이후에는 설정 › 직원 앱 도입 탭(내 정보 옆)에서 다시 볼 수 있어요."
        : "직원 앱 도입을 다시 열까요?";
      if (!(await askConfirm({ message, confirmLabel: completed ? "완료" : "다시 열기" }))) {
        return;
      }
      try {
        await sdkOf().appOnboarding.setCompleted({ completed });
        toast().success(
          completed ? "직원 앱 도입을 완료 처리했어요" : "직원 앱 도입을 다시 열었어요",
        );
        await this.loadSummary();
        window.dispatchEvent(new Event("app-onboarding-changed"));
      } catch (err) {
        showError(errorMessage(err, "처리하지 못했어요."));
      }
    },

    async downloadReport() {
      try {
        const report = await sdkOf().appOnboarding.report();
        const xlsx = await import("xlsx");
        const wb = xlsx.utils.book_new();
        for (const sheet of buildReportSheets(report, fmtDateTime)) {
          xlsx.utils.book_append_sheet(wb, xlsx.utils.aoa_to_sheet(sheet.rows), sheet.name);
        }
        const day = new Date().toISOString().slice(0, 10);
        xlsx.writeFile(wb, `직원앱_배포_보고_${day}.xlsx`);
      } catch (err) {
        showError(errorMessage(err, "보고서를 만들지 못했어요."));
      }
    },

    /** 종이 안내문(QR) 인쇄 — 연락처 없는 직원용 */
    async printPaperGuide() {
      try {
        const QRCode = await import("qrcode");
        const link = this.summary?.downloadUrl ?? null;
        const hospitalCode = this.summary?.hospitalCode ?? null;
        const organizationName =
          (Alpine.store("hereby").currentUser as { organizationName?: string } | null)
            ?.organizationName ?? "";
        const html = link
          ? paperGuideHtml({
              organizationName,
              hospitalCode,
              link,
              qrDataUrl: await QRCode.toDataURL(link, { margin: 1, width: 440 }),
              qrTargets: [],
            })
          : paperGuideHtml({
              organizationName,
              hospitalCode,
              link: null,
              qrDataUrl: null,
              qrTargets: [
                {
                  label: "iPhone (App Store)",
                  dataUrl: await QRCode.toDataURL(WORKS_APP_STORE_URL, { margin: 1, width: 440 }),
                },
                {
                  label: "Android (Google Play)",
                  dataUrl: await QRCode.toDataURL(WORKS_PLAY_STORE_URL, { margin: 1, width: 440 }),
                },
              ],
            });
        const win = window.open("", "_blank", "width=820,height=1000");
        if (!win) {
          showError("팝업이 차단됐어요. 팝업을 허용한 뒤 다시 눌러 주세요.");
          return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
      } catch (err) {
        showError(errorMessage(err, "안내문을 만들지 못했어요."));
      }
    },
  };
}

// ─── 안내 발송 모달 ──────────────────────────────────────────────────────────

const MODE_LABELS: Record<AppGuideTargetMode, string> = {
  [AppGuideTargetMode.EXPLICIT]: "선택한 직원",
  [AppGuideTargetMode.ALL]: "재직 직원 전체",
  [AppGuideTargetMode.UNSENT]: "아직 안내를 받지 않은 직원",
  [AppGuideTargetMode.INCOMPLETE]: "앱 설치·첫 로그인이 안 끝난 직원(재안내)",
  [AppGuideTargetMode.FAILED]: "마지막 발송이 실패한 직원",
};

export function appGuideSendModal() {
  return {
    open: false,
    mode: AppGuideTargetMode.EXPLICIT as AppGuideTargetMode,
    employeeIds: [] as string[],
    teamIds: undefined as string[] | undefined,
    channels: [AppGuideChannel.SMS] as AppGuideChannel[],
    preview: null as GuidePreview | null,
    previewLoading: false,
    previewTab: "sms" as "sms" | "email",
    sending: false,
    result: null as GuideSendResult | null,
    testSending: false,
    testResult: null as { channel: string; status: string; error: string | null }[] | null,

    init() {
      window.addEventListener("app-guide-send-open", (e) => {
        const detail =
          (
            e as CustomEvent<{
              mode?: AppGuideTargetMode;
              employeeIds?: string[];
              teamIds?: string[];
            }>
          ).detail ?? {};
        this.mode = detail.mode ?? AppGuideTargetMode.EXPLICIT;
        this.employeeIds = detail.employeeIds ?? [];
        this.teamIds = detail.teamIds;
        this.result = null;
        this.testResult = null;
        this.open = true;
        this.loadPreview();
      });
    },

    get modeLabel(): string {
      const base = MODE_LABELS[this.mode];
      return this.mode === AppGuideTargetMode.EXPLICIT
        ? `${base} ${this.employeeIds.length}명`
        : base;
    },

    get canSend(): boolean {
      if (
        this.channels.length === 0 ||
        this.sending ||
        this.hospitalCodeMissing ||
        this.unknownPlaceholders.length > 0 ||
        this.passwordPlaceholderMissing
      ) {
        return false;
      }
      return this.mode !== AppGuideTargetMode.EXPLICIT || this.employeeIds.length > 0;
    },

    /** 문구가 {회사코드} 를 쓰는데 코드가 없다 — 서버도 거부하므로 보내기 전에 막고 도입 설정으로 안내한다 */
    get hospitalCodeMissing(): boolean {
      return this.preview?.hospitalCodeMissing === true;
    },

    /** 문구에 치환되지 않는 변수({{tempPassword}} 등)가 있다 — 서버도 거부하므로 보내기 전에 막는다(MED-922) */
    get unknownPlaceholders(): string[] {
      return this.preview?.unknownPlaceholders ?? [];
    },

    /** 보낼 채널 문구에 {임시비밀번호} 가 없다 — 서버도 거부한다(비밀번호가 발급되는데 실리지 않아 로그인이 막힘, MED-916) */
    get passwordPlaceholderMissing(): boolean {
      const missing = this.preview?.passwordPlaceholderMissing;
      if (!missing) {
        return false;
      }
      return this.channels.some((c) => (c === AppGuideChannel.SMS ? missing.sms : missing.email));
    },

    get channelWarning(): string {
      if (!this.preview) {
        return "";
      }
      const parts: string[] = [];
      if (this.channels.includes(AppGuideChannel.SMS) && !this.preview.smsConfigured) {
        parts.push("문자 발송 설정(SOLAPI)이 없어 문자는 실패로 기록돼요.");
      }
      if (this.channels.includes(AppGuideChannel.EMAIL) && !this.preview.emailConfigured) {
        parts.push("이메일 발송 설정(Resend)이 없어 이메일은 실패로 기록돼요.");
      }
      return parts.join(" ");
    },

    toggleChannel(ch: AppGuideChannel) {
      this.channels = this.channels.includes(ch)
        ? this.channels.filter((c) => c !== ch)
        : [...this.channels, ch];
    },

    async loadPreview() {
      this.previewLoading = true;
      try {
        const employeeId =
          this.mode === AppGuideTargetMode.EXPLICIT && this.employeeIds.length === 1
            ? this.employeeIds[0]
            : undefined;
        this.preview = await sdkOf().appOnboarding.previewGuide({ employeeId });
      } catch (err) {
        showError(errorMessage(err, "미리보기를 불러오지 못했어요."));
      } finally {
        this.previewLoading = false;
      }
    },

    async sendTest() {
      this.testSending = true;
      this.testResult = null;
      try {
        const r = await sdkOf().appOnboarding.sendGuideTest({ channels: this.channels });
        this.testResult = r.results;
        const ok = r.results.some((x) => x.status === "sent");
        if (ok) {
          toast().success("내 휴대폰·이메일로 테스트를 보냈어요");
        }
      } catch (err) {
        showError(errorMessage(err, "테스트 발송에 실패했어요."));
      } finally {
        this.testSending = false;
      }
    },

    async send() {
      const channelText = this.channels
        .map((c) => (c === AppGuideChannel.SMS ? "문자" : "이메일"))
        .join("·");
      const confirmed = await askConfirm({
        message: `${this.modeLabel}에게 ${channelText}로 앱 안내를 보낼까요?\n아직 비밀번호를 바꾸지 않은 직원은 임시 비밀번호가 새로 발급돼요.`,
        confirmLabel: "발송",
      });
      if (!confirmed) {
        return;
      }
      this.sending = true;
      try {
        this.result = await sdkOf().appOnboarding.sendGuide({
          mode: this.mode,
          employeeIds: this.mode === AppGuideTargetMode.EXPLICIT ? this.employeeIds : undefined,
          teamIds: this.teamIds,
          channels: this.channels,
        });
        toast().success(
          `발송 완료 — 성공 ${this.result.sent}명 · 실패 ${this.result.failed}명 · 건너뜀 ${this.result.skipped}명`,
        );
        window.dispatchEvent(new Event("app-guide-sent"));
      } catch (err) {
        showError(errorMessage(err, "발송에 실패했어요."));
      } finally {
        this.sending = false;
      }
    },

    close() {
      this.open = false;
    },

    channelLabel(ch: string): string {
      return ch === AppGuideChannel.SMS ? "문자" : "이메일";
    },

    statusLabel(status: string): string {
      return { sent: "성공", failed: "실패", skipped: "건너뜀" }[status] ?? status;
    },

    statusClass(status: string): string {
      return (
        { sent: "ds-label--green", failed: "ds-label--red", skipped: "ds-label--light-gray" }[
          status
        ] ?? "ds-label--gray"
      );
    },
  };
}

/** 튜토리얼 단계 라벨(설정 토글용) */
export const TUTORIAL_STEP_LABELS: Record<AppTutorialStep, string> = Object.fromEntries(
  APP_TUTORIAL_STEP_DEFS.map((d) => [d.key, d.title]),
) as Record<AppTutorialStep, string>;
